<script lang="ts">
  import type { Snippet } from 'svelte';
  import type { HTMLButtonAttributes } from 'svelte/elements';

  type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
  type Size = 'sm' | 'md' | 'lg';

  let {
    variant = 'primary',
    size = 'md',
    type = 'button',
    disabled = false,
    fullWidth = false,
    class: className = '',
    children,
    ...rest
  }: {
    variant?: Variant;
    size?: Size;
    type?: HTMLButtonAttributes['type'];
    disabled?: boolean;
    fullWidth?: boolean;
    class?: string;
    children: Snippet;
  } & Omit<HTMLButtonAttributes, 'type' | 'disabled' | 'class' | 'children'> = $props();
</script>

<button
  class="btn btn-{variant} btn-{size} {className}"
  class:full-width={fullWidth}
  {type}
  {disabled}
  {...rest}
>
  {@render children()}
</button>

<style>
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    border: 1px solid transparent;
    border-radius: var(--radius-md);
    font-weight: var(--weight-medium);
    cursor: pointer;
    transition:
      background var(--duration) var(--ease-out),
      border-color var(--duration) var(--ease-out),
      color var(--duration) var(--ease-out),
      transform var(--duration-fast) var(--ease-out);
    white-space: nowrap;
  }

  .btn.full-width {
    width: 100%;
  }

  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn:not(:disabled):active {
    transform: translateY(1px);
  }

  .btn-sm {
    height: var(--control-h-sm);
    padding: 0 var(--space-3);
    font-size: var(--text-sm);
  }

  .btn-md {
    height: var(--control-h);
    padding: 0 var(--space-4);
    font-size: var(--text-sm);
  }

  .btn-lg {
    height: var(--control-h-lg);
    padding: 0 var(--space-5);
    font-size: var(--text-md);
  }

  .btn-primary {
    background: linear-gradient(180deg, var(--brand-400), var(--brand-500));
    color: var(--accent-fg);
    border-color: color-mix(in srgb, var(--brand-300) 40%, transparent);
    box-shadow: var(--shadow-sm);
  }

  .btn-primary:hover:not(:disabled) {
    background: linear-gradient(180deg, var(--brand-300), var(--brand-400));
  }

  .btn-secondary {
    background: var(--surface);
    border-color: var(--border);
    color: var(--text);
  }

  .btn-secondary:hover:not(:disabled) {
    background: var(--bg-hover);
    border-color: var(--border-strong);
  }

  .btn-ghost {
    background: transparent;
    color: var(--text-secondary);
  }

  .btn-ghost:hover:not(:disabled) {
    background: var(--bg-hover);
    color: var(--text);
  }

  .btn-danger {
    background: var(--danger-muted);
    border-color: color-mix(in srgb, var(--danger) 35%, transparent);
    color: var(--danger);
  }

  .btn-danger:hover:not(:disabled) {
    background: color-mix(in srgb, var(--danger) 24%, transparent);
  }
</style>
